/**
 * PURPOSE: Grilla de captura de postas (`PST`): una fila por equipo.
 * RESPONSIBILITIES: Carril, universidad, marca, estado y lugar. Los relevistas no se listan en la tabla:
 *   se ven (y, con permiso de escritura, se eligen) con "Ver relevistas". Viajan como `legs` completas.
 *   Con permiso para agregar, los carriles vacíos (1-8 o los que traiga la prueba) se ven como filas con un "+": un equipo
 *   sin serie de la prueba (conserva sus relevistas) o "Otra institución…" (ventana Agregar institución con ese carril).
 * COLLABORATORS: RoundEditor.jsx, RelayLegsModal.jsx, gridCells.jsx.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/grids/RelayGrid.jsx
 */
import React, { useMemo, useState } from "react";
import { FiUsers } from "react-icons/fi";

import { normalizeTimeMark } from "../../../../utils/athleticsMarks.js";
import { institutionAbrev } from "../../labels.js";
import { compactSelectClass } from "../ui.jsx";
import {
  isServerInvalidCell,
  trackMarkPatch,
  trackStatusPatch,
} from "../../../../utils/athleticsRoundDraft.js";
import { TrackStatusSelect } from "./TrackGrid.jsx";
import RelayLegsModal from "./RelayLegsModal.jsx";
import {
  CellInput,
  FrozenScroll,
  InstitutionCell,
  NumberCell,
  PlaceCell,
  RowActions,
  buildFrozenColumns,
  rowToneClass,
  tdClass,
  thCenterClass,
  thClass,
} from "./gridCells.jsx";

const TRACK_MARK_HELP =
  "Tiempo: 45.12 · 3:21.50 (la coma vale como punto). También 4512 → 45.12. Escribe DNS, DNF o DQ para marcar el estado.";

const RelayGrid = ({
  prueba,
  round,
  draft,
  setRow,
  readOnly,
  localInvalidKeys,
  serverInvalid,
  changedIds,
  canRemove,
  rowActions,
  nav,
  unassigned = [],
  onAddEntry,
  onSearchAtLane,
  poolBlocked = false,
}) => {
  const entries = useMemo(() => (Array.isArray(round?.entries) ? round.entries : []), [round]);
  const showLane = prueba?.laneMode !== "none";
  const frozen = buildFrozenColumns([showLane ? ["lane", 4] : null, ["order", 4], ["team", 12]]);
  const mixed =
    prueba?.branch?.code === "X" ||
    entries.some((entry) => (entry.participant?.members || []).some((member) => member?.gender));
  const [legsFor, setLegsFor] = useState(null);
  const legsEntry = entries.find((entry) => entry.id === legsFor) || null;

  const pool = typeof onAddEntry === "function" ? (Array.isArray(unassigned) ? unassigned : []).filter((item) => item.teamId != null) : [];
  const canSearch = typeof onSearchAtLane === "function";
  const canFill = showLane && !readOnly && (pool.length > 0 || canSearch);
  const laneCount = Math.max(Number(prueba?.config?.lanes) || 8, ...entries.map((entry) => Number(entry.lane) || 0));
  // Orden de carril con los vacíos intercalados (solo si se puede agregar); sin carril, al final.
  const slots = [];
  if (canFill) {
    for (let lane = 1; lane <= laneCount; lane += 1) {
      const here = entries.filter((entry) => Number(entry.lane) === lane);
      if (here.length) here.forEach((entry) => slots.push({ entry }));
      else slots.push({ lane });
    }
    entries.filter((entry) => !(Number(entry.lane) >= 1 && Number(entry.lane) <= laneCount)).forEach((entry) => slots.push({ entry }));
  } else {
    entries.forEach((entry) => slots.push({ entry }));
  }
  let nextIndex = 0;
  slots.forEach((slot) => {
    if (slot.entry) {
      slot.index = nextIndex;
      nextIndex += 1;
    }
  });
  const trailingColumns = 4 + (canRemove ? 1 : 0);

  const fillLane = (lane, value) => {
    if (value === "search") onSearchAtLane(lane);
    else if (value.startsWith("team:")) {
      const item = pool.find((candidate) => String(candidate.teamId) === value.slice(5));
      if (item) onAddEntry(item, lane);
    }
  };

  const renderFreeLane = (lane) => (
    <tr key={`lane-${lane}`} className="border-b border-slate-100 bg-white last:border-b-0">
      <td {...frozen("lane", `${tdClass} text-center`)}>
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-slate-300 text-sm font-black text-slate-400">
          {lane}
        </span>
      </td>
      <td {...frozen("order", tdClass)} />
      <td {...frozen("team", tdClass)}>
        <select
          value=""
          onChange={(event) => fillLane(lane, event.target.value)}
          aria-label={`Agregar un equipo al carril ${lane}`}
          className={`${compactSelectClass} w-full text-xs`}
        >
          <option value="">+ Carril libre: agregar…</option>
          {pool.length ? (
            <optgroup label={poolBlocked ? "Equipos sin serie (guarda los cambios primero)" : "Equipos sin serie"}>
              {pool.map((item) => (
                <option key={item.teamId} value={`team:${item.teamId}`} disabled={poolBlocked}>
                  {institutionAbrev(item.institution) || item.name}
                  {Number(item.legs) ? ` · ${item.legs} relevistas` : ""}
                </option>
              ))}
            </optgroup>
          ) : null}
          {canSearch ? (
            <option value="search" disabled={poolBlocked}>
              {poolBlocked ? "Otra institución… (guarda los cambios primero)" : "Otra institución…"}
            </option>
          ) : null}
        </select>
      </td>
      <td colSpan={trailingColumns} className={tdClass} />
    </tr>
  );

  const renderRow = (entry, index) => {
    const row = draft.rows[entry.id];
    if (!row) return null;
    const serverRow = serverInvalid?.rowId === entry.id;
    const teamName = entry.participant?.name || entry.participant?.institution?.name || "Equipo";
    return (
      <tr
        key={entry.id}
        className={`border-b border-slate-100 last:border-b-0 ${rowToneClass({
          serverInvalid: serverRow,
          changed: changedIds.has(entry.id),
        })}`}
      >
        {showLane ? (
          <td {...frozen("lane", `${tdClass} text-center`)}>
            <NumberCell
              value={row.lane}
              onChange={(value) => setRow(entry.id, { lane: value })}
              disabled={readOnly}
              ariaLabel={`Carril de ${teamName}`}
            />
          </td>
        ) : null}
        <td {...frozen("order", `${tdClass} text-center`)}>
          <NumberCell
            value={row.order}
            onChange={(value) => setRow(entry.id, { order: value })}
            disabled={readOnly}
            ariaLabel={`Orden de ${teamName}`}
          />
        </td>
        <td {...frozen("team", tdClass)}>
          <span className="block text-sm font-bold text-slate-800">{teamName}</span>
          <InstitutionCell institution={entry.participant?.institution} />
        </td>
        <td className={`${tdClass} text-center`}>
          <button
            type="button"
            onClick={() => setLegsFor(entry.id)}
            title={`${(row.legs || []).length} de 4 postas con relevista asignado`}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg bg-indigo-50 px-2 py-1 text-[11px] font-bold text-indigo-700 transition hover:bg-indigo-100"
          >
            <FiUsers aria-hidden="true" />
            Ver relevistas
          </button>
        </td>
        <td className={`${tdClass} text-center`}>
          <CellInput
            value={row.mark}
            onChange={(raw) => setRow(entry.id, (current) => trackMarkPatch(current, raw))}
            onBlur={() => setRow(entry.id, (current) => ({ mark: normalizeTimeMark(current.mark) }))}
            disabled={readOnly}
            invalid={isServerInvalidCell(serverInvalid, { rowId: entry.id, kind: "mark", cellValue: row.mark })}
            softInvalid={localInvalidKeys.has(`${entry.id}|mark`)}
            inputRef={nav.register(index, "mark")}
            onKeyDown={nav.onKeyDown(index, "mark")}
            placeholder="0.00"
            title={TRACK_MARK_HELP}
            ariaLabel={`Marca de ${teamName}`}
          />
        </td>
        <td className={`${tdClass} text-center`}>
          <TrackStatusSelect
            value={row.status}
            onChange={(value) => setRow(entry.id, trackStatusPatch(value))}
            disabled={readOnly}
            ariaLabel={`Estado de ${teamName}`}
          />
        </td>
        <td className={`${tdClass} text-center`}>
          <PlaceCell place={entry.placeInRound} />
        </td>
        {canRemove ? (
          <td className={`${tdClass} text-center`}>
            <RowActions name={teamName} {...rowActions(entry)} />
          </td>
        ) : null}
      </tr>
    );
  };

  return (
    <div>
      <FrozenScroll>
        <table className="w-full min-w-[640px] table-auto">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {showLane ? <th {...frozen("lane", thCenterClass)}>Carril</th> : null}
              <th {...frozen("order", thCenterClass)}>Orden</th>
              <th {...frozen("team", thClass)}>Universidad</th>
              <th className={thCenterClass}>Relevistas</th>
              <th className={thCenterClass}>Marca</th>
              <th className={thCenterClass}>Estado</th>
              <th className={thCenterClass}>Lugar</th>
              {canRemove ? <th className={thCenterClass} aria-label="Acciones" /> : null}
            </tr>
          </thead>
          <tbody>
            {slots.map((slot) => (slot.entry ? renderRow(slot.entry, slot.index) : renderFreeLane(slot.lane)))}
          </tbody>
        </table>
      </FrozenScroll>

      <RelayLegsModal
        open={Boolean(legsEntry)}
        team={legsEntry?.participant}
        pruebaId={prueba?.id}
        legs={legsEntry ? draft.rows[legsEntry.id]?.legs : []}
        mixed={mixed}
        readOnly={readOnly}
        onClose={() => setLegsFor(null)}
        onConfirm={(legs) => {
          if (legsEntry) setRow(legsEntry.id, { legs });
          setLegsFor(null);
        }}
      />
    </div>
  );
};

export default RelayGrid;
